import { useState, useEffect } from "react";
import { ArrowRight, CreditCard, Search, Wallet, Loader2, Info } from "lucide-react";
import SEO from "@/components/SEO";
import api from "@/lib/api";
import { toast } from "sonner";

export default function PayInvoice() {
  const [config, setConfig] = useState({ generic_payment_link: "", live_stripe: false });

  useEffect(() => {
    api.get("/config").then((r)=>setConfig(r.data)).catch(()=>{});
  }, []);

  return (
    <div data-testid="pay-invoice-page">
      <SEO
        title="Pay an Invoice Securely | Park Home Support"
        description="Pay park home support invoices, agreed works or support-related charges securely online via Stripe."
        canonical="https://parkhomesupport.co.uk/pay"
      />
      <section className="hero-gradient section-pad">
        <div className="container-phs">
          <span className="eyebrow">Pay an Invoice</span>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-3 max-w-3xl text-[#071A33] leading-[1.05]">
            Make a Secure <span className="text-[#064E2E]">Payment.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-[#334155]">
            Pay invoices, agreed works or support-related charges securely online.
          </p>

          <div className="mt-8 card-default border-l-4 border-[#0F766E] bg-[#F0FBF6] max-w-3xl" data-testid="pay-info">
            <div className="flex gap-3 items-start">
              <Info size={20} className="text-[#0F766E] mt-0.5 shrink-0"/>
              <p className="text-sm text-[#0B3D2A] leading-relaxed">
                Membership fees are separate from inspection, repair or contractor costs.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="section-pad bg-white">
        <div className="container-phs grid lg:grid-cols-3 gap-6 lg:gap-8">
          <PayNowCard link={config.generic_payment_link}/>
          <InvoiceLookupCard/>
          <CustomPaymentCard/>
        </div>
      </section>
    </div>
  );
}

function PayNowCard({ link }) {
  return (
    <article className="card-default flex flex-col" data-testid="pay-now-card">
      <div className="w-11 h-11 bg-[#064E2E] text-white rounded-xl flex items-center justify-center"><CreditCard/></div>
      <h3 className="font-display text-2xl mt-5 text-[#071A33]">Pay an Invoice</h3>
      <p className="mt-2 text-[#334155]">Use our secure Stripe payment link if you've been given a payment instruction.</p>
      <div className="mt-auto pt-6">
        {link ? (
          <a href={link} target="_blank" rel="noopener noreferrer" className="btn-primary w-full" data-testid="pay-now-btn">
            Pay Securely <ArrowRight size={16}/>
          </a>
        ) : (
          <button disabled className="btn-primary w-full" data-testid="pay-now-disabled">Stripe link not configured</button>
        )}
      </div>
    </article>
  );
}

function InvoiceLookupCard() {
  const [form, setForm] = useState({ invoice_number: "", name: "", email: "", phone: "" });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const update = (k,v) => setForm(f=>({...f,[k]:v}));

  async function onSubmit(e) {
    e.preventDefault();
    setSubmitting(true);
    setResult(null);
    try {
      const { data } = await api.post("/payments/invoice-lookup", form);
      setResult(data);
      if (data.found && form.invoice_number) {
        // Redirect to the rich invoice pay page so the customer sees full breakdown
        window.location.href = `/pay/${encodeURIComponent(form.invoice_number)}`;
        return;
      }
      if (!data.found) toast("Logged. We'll be in touch.", { icon: "📌" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Lookup failed");
    } finally { setSubmitting(false); }
  }

  return (
    <article className="card-default flex flex-col" data-testid="invoice-lookup-card">
      <div className="w-11 h-11 bg-[#064E2E] text-white rounded-xl flex items-center justify-center"><Search/></div>
      <h3 className="font-display text-2xl mt-5 text-[#071A33]">Enter Invoice Number</h3>
      <p className="mt-2 text-[#334155]">Look up an invoice we've issued to you and pay it directly.</p>
      <form onSubmit={onSubmit} className="mt-5 space-y-4">
        <input className="field-input" required placeholder="Invoice number" value={form.invoice_number} onChange={e=>update("invoice_number", e.target.value)} data-testid="lookup-invoice-number"/>
        <input className="field-input" required placeholder="Name" value={form.name} onChange={e=>update("name", e.target.value)} data-testid="lookup-name"/>
        <input className="field-input" required type="email" placeholder="Email" value={form.email} onChange={e=>update("email", e.target.value)} data-testid="lookup-email"/>
        <input className="field-input" placeholder="Phone (optional)" value={form.phone} onChange={e=>update("phone", e.target.value)} data-testid="lookup-phone"/>
        <button disabled={submitting} className="btn-primary w-full" data-testid="lookup-submit-btn">
          {submitting ? <><Loader2 className="animate-spin" size={16}/> Searching…</> : "Look Up & Pay"}
        </button>
      </form>
      {result && (
        <div className={`mt-4 rounded-xl border p-3 text-sm ${result.found ? "border-[#0F766E] bg-[#F0FBF6]" : "border-[#D7E7DF] bg-white"}`} data-testid="lookup-result">
          {result.found ? (
            <>
              <p className="font-medium text-[#071A33]">Invoice {result.invoice_number}</p>
              <p>Amount: £{result.amount} — Status: {result.status}</p>
              {result.payment_url && <a className="link-underline" href={result.payment_url}>Pay this invoice →</a>}
            </>
          ) : (
            <p>{result.message}</p>
          )}
        </div>
      )}
    </article>
  );
}

function CustomPaymentCard() {
  const [form, setForm] = useState({ amount: "", reference: "", name: "", email: "" });
  const [submitting, setSubmitting] = useState(false);
  const [pendingMsg, setPendingMsg] = useState("");
  const update = (k,v) => setForm(f=>({...f,[k]:v}));

  async function onSubmit(e) {
    e.preventDefault();
    setSubmitting(true);
    setPendingMsg("");
    try {
      const { data } = await api.post("/payments/custom", {
        amount: Number(form.amount),
        reference: form.reference,
        name: form.name,
        email: form.email,
        origin_url: window.location.origin,
      });
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
        return;
      }
      setPendingMsg(data.message || "Payment record created.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start payment");
    } finally { setSubmitting(false); }
  }

  return (
    <article className="card-default flex flex-col" data-testid="custom-payment-card">
      <div className="w-11 h-11 bg-[#064E2E] text-white rounded-xl flex items-center justify-center"><Wallet/></div>
      <h3 className="font-display text-2xl mt-5 text-[#071A33]">Make a General Payment</h3>
      <p className="mt-2 text-[#334155]">Pay any agreed amount using a one-off Stripe checkout.</p>
      <form onSubmit={onSubmit} className="mt-5 space-y-4">
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#475569]">£</span>
          <input className="field-input pl-7" required type="number" min="1" step="0.01" placeholder="Amount" value={form.amount} onChange={e=>update("amount", e.target.value)} data-testid="custom-amount"/>
        </div>
        <input className="field-input" required placeholder="Reference (job, invoice, etc.)" value={form.reference} onChange={e=>update("reference", e.target.value)} data-testid="custom-reference"/>
        <input className="field-input" required placeholder="Name" value={form.name} onChange={e=>update("name", e.target.value)} data-testid="custom-name"/>
        <input className="field-input" required type="email" placeholder="Email" value={form.email} onChange={e=>update("email", e.target.value)} data-testid="custom-email"/>
        <button disabled={submitting} className="btn-primary w-full" data-testid="custom-submit-btn">
          {submitting ? <><Loader2 className="animate-spin" size={16}/> Starting…</> : "Pay Securely"}
        </button>
      </form>
      {pendingMsg && (
        <div className="mt-4 bg-[#F7F4ED] border border-[#D7E7DF] rounded-xl p-3 text-sm" data-testid="custom-pending">{pendingMsg}</div>
      )}
    </article>
  );
}
